import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";

import { ErroApi, get } from "@/api/client";
import { useAceitarConvite } from "@/api/sessao";
import soapLogo from "@/assets/soap-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/convite/$token")({
  component: ConvitePage,
});

interface DadosConvite {
  email: string;
  nome: string;
  perfil: string;
}

/**
 * Primeiro acesso por convite (RF-50).
 *
 * Sem mailer nesta leva, o Admin gera o link na tela de Acessos e o envia por
 * fora. O token é single-use e expira em horas — por isso a tela precisa
 * distinguir "expirado" de "já usado" com clareza.
 */
function ConvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const aceitar = useAceitarConvite();
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");

  const convite = useQuery({
    queryKey: ["convite", token],
    queryFn: () => get<DadosConvite>("/auth/convite", { token }),
    retry: false,
  });

  const senhasDiferem = confirmacao.length > 0 && senha !== confirmacao;
  const curta = senha.length > 0 && senha.length < 8;
  const podeEnviar = senha.length >= 8 && senha === confirmacao && !aceitar.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeEnviar) return;
    aceitar.mutate({ token, senha }, { onSuccess: () => navigate({ to: "/loja", replace: true }) });
  };

  const erroAceite =
    aceitar.error instanceof ErroApi
      ? aceitar.error.mensagem
      : aceitar.error
        ? "Falha ao criar o acesso."
        : null;

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center px-4"
      style={{ backgroundColor: "#0a0a0a" }}
    >
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-2">
          <img src={soapLogo} alt="SOAP" className="h-20 w-auto" />
          <span
            className="text-sm font-bold uppercase tracking-[0.28em]"
            style={{ color: "#FF5A00" }}
          >
            Flow
          </span>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
          {convite.isLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-white/60">
              <Loader2 className="h-4 w-4 animate-spin" /> Validando convite…
            </div>
          )}

          {convite.isError && (
            <div className="space-y-4 text-center">
              <AlertCircle className="mx-auto h-8 w-8 text-red-400" />
              <div>
                <p className="text-sm font-medium text-white">Convite inválido ou expirado</p>
                <p className="mt-1 text-xs text-white/50">
                  Links de convite valem por poucas horas e só podem ser usados uma vez. Peça um
                  novo ao administrador.
                </p>
              </div>
            </div>
          )}

          {convite.data && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="rounded-md border border-white/10 bg-black/30 px-3 py-2">
                <p className="text-sm font-medium text-white">{convite.data.nome}</p>
                <p className="text-xs text-white/50">{convite.data.email}</p>
                <p className="mt-1 text-[10px] uppercase tracking-wider text-[#FF5A00]">
                  {convite.data.perfil}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="senha" className="text-white/80">
                  Crie sua senha
                </Label>
                <Input
                  id="senha"
                  type="password"
                  autoComplete="new-password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="border-white/15 bg-black/40 text-white"
                  required
                />
                {curta && <p className="text-[11px] text-white/50">Ao menos 8 caracteres.</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmacao" className="text-white/80">
                  Confirme a senha
                </Label>
                <Input
                  id="confirmacao"
                  type="password"
                  autoComplete="new-password"
                  value={confirmacao}
                  onChange={(e) => setConfirmacao(e.target.value)}
                  className="border-white/15 bg-black/40 text-white"
                  required
                />
                {senhasDiferem && (
                  <p className="text-[11px] text-red-300">As senhas não conferem.</p>
                )}
              </div>

              {erroAceite && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {erroAceite}
                </div>
              )}

              <Button
                type="submit"
                disabled={!podeEnviar}
                className="w-full font-semibold text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "#FF5A00" }}
              >
                {aceitar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Criar acesso e entrar
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
